import { useCallback, useRef } from 'react';

function mint(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  const bytes = new Uint8Array(16);
  c?.getRandomValues?.(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * One Idempotency-Key per logical submit.
 *
 * The rule the API enforces is that a retried request must not create a
 * second clinical record. That only works if the retry carries the SAME key
 * — a form that mints a fresh UUID on every click of "Save" has defeated the
 * mechanism while appearing to honour it. So the key lives with the form:
 * `key()` returns the current one, unchanged across retries of the same
 * attempt; `rotate()` is called after a success (or when the user changes
 * what they are submitting) so the next distinct operation gets its own.
 *
 * Deliberately not tied to the form's contents: the server already hashes
 * the body and refuses a key reused with a different one, and that is the
 * right layer for it.
 */
/** One fresh key, for a form that sends several creates in one go. */
export const newIdempotencyKey = mint;

export function useIdempotencyKey() {
  const ref = useRef<string | null>(null);
  const key = useCallback(() => {
    if (!ref.current) ref.current = mint();
    return ref.current;
  }, []);
  const rotate = useCallback(() => { ref.current = null; }, []);
  return { key, rotate };
}
